/**
 * Modular Story Engine Subsystem (src/story/StoryEngine.ts).
 * 
 * Compiles human-readable story scenes (type: "story") with cast definitions, 
 * directional movements ("left", "right", "up", "down"), speech dialogues ("narration"), 
 * character mounts, group formations, and environment backdrops into ASAE scene AST primitives.
 */
import { SceneSchema, ObjectSchema, AudioScriptSchema, StoryStepSchema, StoryCastSchema } from '../parser/Schema';
import { StoryHelpers } from './StoryHelpers';
import { AssetService } from '../services/AssetService';

export class StoryEngine {
  /**
   * Checks if a given scene is a Story Scene (type === 'story').
   */
  public static isStoryScene(scene: SceneSchema): boolean {
    return scene.type === 'story' || Boolean(scene.cast && scene.cast.length > 0) || Boolean(scene.steps && scene.steps.length > 0);
  }

  /**
   * Compiles a Story Scene into normalized ASAE visual objects, audio tracks, and action keyframes.
   */
  public static compileStoryScene(scene: SceneSchema): SceneSchema {
    if (!scene.objects) scene.objects = [];
    if (!scene.audio) scene.audio = [];

    // 1. Build Environment Background Objects
    if (scene.environment) {
      const envObj = this.buildEnvironmentObject(scene.environment);
      if (envObj) {
        scene.objects.unshift(envObj);
      }
    }

    // 2. Build Cast Character & Mount Objects
    const castMap = new Map<string, ObjectSchema>();
    if (scene.cast && scene.cast.length > 0) {
      scene.cast.forEach(castItem => {
        const charObj = this.buildCastObject(castItem);
        scene.objects!.push(charObj);
        castMap.set(castItem.id, charObj);
      });
    }

    // 3. Translate Story Steps into Object Actions, Keyframe Animations, & Narration Audio
    if (scene.steps && scene.steps.length > 0) {
      scene.steps.forEach(step => {
        this.processStoryStep(step, castMap, scene.objects!, scene.audio!);
      });
    }

    return scene;
  }

  /**
   * Constructs background environment visual object.
   */
  private static buildEnvironmentObject(env: any): ObjectSchema {
    const bgName = env.name || env.preset || 'fort_battleground';
    const cachedAsset = AssetService.getCachedAsset(bgName);

    if (cachedAsset && cachedAsset.type === 'image' && cachedAsset.src) {
      return {
        id: 'bg-environment',
        type: 'image',
        layer: 'background',
        x: 0,
        y: 0,
        scaleX: 1,
        scaleY: 1,
        props: {
          src: cachedAsset.src,
          width: 1920,
          height: 1080
        }
      };
    }

    // Default SVG / Canvas preset background fallback
    return {
      id: 'bg-environment',
      type: 'shape',
      layer: 'background',
      x: 0,
      y: 0,
      props: {
        kind: 'rect',
        width: 1920,
        height: 1080,
        fill: env.skyColor || '#0f172a',
        stroke: 'none'
      }
    };
  }

  /**
   * Constructs character rig object for a cast member.
   */
  private static buildCastObject(cast: StoryCastSchema): ObjectSchema {
    const pos = StoryHelpers.resolveStagePosition(cast.startPosition || cast.x, cast.x || 0, cast.y || 250);
    const charName = cast.name || cast.preset || 'teacher';
    const cachedAsset = AssetService.getCachedAsset(charName);

    const isImage = cachedAsset?.type === 'image' && Boolean(cachedAsset.src);

    return {
      id: cast.id,
      type: isImage ? 'image' : 'character',
      layer: 'characters',
      x: pos.x,
      y: pos.y,
      scaleX: (cast.scale || 1) * (cast.direction === 'left' ? -1 : 1),
      scaleY: cast.scale || 1,
      props: {
        preset: cast.preset || cast.name || 'teacher',
        displayName: cast.displayName || cast.id,
        src: isImage ? cachedAsset!.src : undefined,
        costume: cast.costume,
        props: cast.props,
        color: cast.color,
        voice: cast.voice
      },
      actions: [],
      animations: []
    };
  }

  /**
   * Processes a single human-readable story step action.
   */
  private static processStoryStep(
    step: StoryStepSchema,
    castMap: Map<string, ObjectSchema>,
    objects: ObjectSchema[],
    audio: AudioScriptSchema[]
  ): void {
    const actionType = (step.action || 'speak').toLowerCase();
    const time = step.time || 0;
    const duration = step.duration || 3;

    switch (actionType) {
      case 'speak':
      case 'narration':
      case 'dialogue': {
        const speakerId = step.speakerId || step.characterId;
        if (step.narration) {
          audio.push({
            id: `audio-${step.step || Math.random().toString(36).substr(2, 6)}`,
            time,
            type: 'speech',
            text: step.narration,
            speakerId,
            emotion: step.emotion || 'neutral',
            subtitles: step.subtitles !== false
          });
        }
        if (speakerId && castMap.has(speakerId)) {
          const charObj = castMap.get(speakerId)!;
          if (!charObj.actions) charObj.actions = [];
          charObj.actions.push({
            time,
            duration,
            type: 'talk',
            emotion: step.emotion || 'neutral',
            params: { pose: step.pose }
          });
        }
        break;
      }

      case 'ride':
      case 'walk':
      case 'run':
      case 'move': {
        const charId = step.characterId || step.mountId;
        if (charId && castMap.has(charId)) {
          const charObj = castMap.get(charId)!;
          const startPos = { x: charObj.x || 0, y: charObj.y || 250 };
          const targetPos = StoryHelpers.calculateTargetPosition(startPos, step.direction || 'right', step.distance);

          if (!charObj.animations) charObj.animations = [];
          charObj.animations.push({
            property: 'x',
            keyframes: [
              { time, value: startPos.x, easing: 'easeInOut' },
              { time: time + duration, value: targetPos.x, easing: 'easeInOut' }
            ]
          });
          charObj.x = targetPos.x;

          if (!charObj.actions) charObj.actions = [];
          charObj.actions.push({
            time,
            duration,
            type: actionType === 'ride' ? 'run' : actionType,
            params: { gait: step.gait || 'gallop', speed: step.speed || 'fast' }
          });
        }
        break;
      }

      case 'mount': {
        if (step.characterId && step.targetId && castMap.has(step.characterId) && castMap.has(step.targetId)) {
          const charObj = castMap.get(step.characterId)!;
          const mountObj = castMap.get(step.targetId)!;
          if (!charObj.actions) charObj.actions = [];
          charObj.actions.push({
            time,
            duration,
            type: 'enter',
            params: { mountId: step.targetId, offsetY: -40 }
          });
          charObj.x = mountObj.x;
          charObj.y = (mountObj.y || 250) - 40;
        }
        break;
      }

      case 'follow': {
        if (step.leaderId && step.characters && castMap.has(step.leaderId)) {
          const leaderObj = castMap.get(step.leaderId)!;
          const leaderPos = { x: leaderObj.x || 0, y: leaderObj.y || 250 };
          const followerPositions = StoryHelpers.computeFormationPositions(leaderPos, step.characters.length, step.formation);

          step.characters.forEach((fId, idx) => {
            if (castMap.has(fId)) {
              const followerObj = castMap.get(fId)!;
              const target = followerPositions[idx];
              if (!followerObj.animations) followerObj.animations = [];
              followerObj.animations.push({
                property: 'x',
                keyframes: [
                  { time, value: followerObj.x || -800, easing: 'easeInOut' },
                  { time: time + duration, value: target.x, easing: 'easeInOut' }
                ]
              });
              followerObj.x = target.x;
            }
          });
        }
        break;
      }

      case 'fx':
      case 'particle': {
        const pos = StoryHelpers.resolveStagePosition(step.position || 'center', step.x || 0, step.y || 300);
        objects.push({
          id: `fx-${Math.random().toString(36).substr(2, 6)}`,
          type: 'sparkles',
          layer: 'fx',
          x: pos.x,
          y: pos.y,
          appearTime: time,
          disappearTime: time + duration,
          props: { preset: step.preset || 'dust_cloud' }
        });
        break;
      }

      default:
        break;
    }

    if (step.soundName) {
      audio.push({
        id: `sfx-${Math.random().toString(36).substr(2, 6)}`,
        time,
        type: 'sfx',
        soundName: step.soundName,
        volume: 0.8
      });
    }
  }
}
